var PCGG = (function () {
  var TICK = 1000 / 60;
  var SETTLE_MS = 900;
  var FRICTION = 0.999;       // per-tick rolling resistance while on the floor
  var ROUND_CAP_MS = 8000;    // hard round cap: no round can outlive this
  var MAX_ROLL_V = 0.0015;    // horizontal speed ceiling on the floor (tunneling guard only)

  function makeState(level) {
    var blocks = level.blocks.map(function (b) {
      return { x: b.x, y: b.y, w: b.w, h: b.h, hp: b.hp, vx: 0, vy: 0, rot: 0 };
    });
    return {
      level: level, blocks: blocks, acc: 0, t: 0, firstImpact: null,
      ball: { x: level.sling.x, y: level.sling.y, vx: 0, vy: 0, live: false }
    };
  }

  function launch(state, vx, vy) {
    var p = state.level.maxPower;
    var m = Math.sqrt(vx * vx + vy * vy);
    if (m > p) { vx = vx / m * p; vy = vy / m * p; }
    state.ball.vx = vx; state.ball.vy = vy; state.ball.live = true;
  }

  // The floor is a static valley. Its low point is the level constant
  // (level.valleyX, level.ground); each wall is a parabola, so the surface
  // slope is continuous everywhere and exactly zero at the low point. Nothing
  // here reads live block state, so the floor cannot drift with the debris.
  // y grows downward, so subtracting the wall term lifts the floor away from
  // the low point in both directions.
  function groundAt(level, x) {
    var d = x - level.valleyX;
    var k = d < 0 ? level.valleyKLeft : level.valleyKRight;
    return level.ground - k * d * d;
  }

  // d(groundAt)/dx. Positive means the floor descends to the right (downhill
  // is +x); negative means it climbs to the right. Zero at the low point.
  function groundSlopeAt(level, x) {
    var d = x - level.valleyX;
    var k = d < 0 ? level.valleyKLeft : level.valleyKRight;
    return -2 * k * d;
  }

  function hits(ball, b) {
    return ball.x > b.x - b.w / 2 && ball.x < b.x + b.w / 2 &&
           ball.y > b.y - b.h / 2 && ball.y < b.y + b.h / 2;
  }

  function step(state) {
    var L = state.level, ball = state.ball;
    state.t += TICK;
    if (ball.live) {
      ball.vy += L.gravity * TICK;
      ball.x += ball.vx * TICK;
      ball.y += ball.vy * TICK;
      var g = groundAt(L, ball.x);
      if (ball.y >= g) {
        ball.y = g;
        var m = groundSlopeAt(L, ball.x);
        var d = 1 + m * m;
        // Project the velocity onto the floor tangent: the component driving
        // into the surface is absorbed, the along-surface component survives.
        // A ball already following the floor has vy = m * vx, for which this
        // is the identity, so it costs nothing tick to tick.
        var vt = (ball.vx + ball.vy * m) / d;
        // Gravity resolved along that same surface. Downhill is +x when the
        // slope is positive, so this alone reverses a ball that climbs the far
        // wall: it decelerates, passes through zero, and comes back.
        vt += L.gravity * (m / d) * TICK;
        vt *= FRICTION;
        if (vt > MAX_ROLL_V) vt = MAX_ROLL_V;
        else if (vt < -MAX_ROLL_V) vt = -MAX_ROLL_V;
        ball.vx = vt;
        ball.vy = m * vt;
      }
      for (var i = 0; i < state.blocks.length; i++) {
        var b = state.blocks[i];
        if (b.hp > 0 && hits(ball, b)) {
          b.hp = 0;
          b.vx = ball.vx * 0.6; b.vy = -Math.abs(ball.vx) * 0.3;
          if (state.firstImpact === null) state.firstImpact = state.t;
          ball.vx *= 0.45; ball.vy *= 0.45;
        }
      }
    }
    for (var j = 0; j < state.blocks.length; j++) {
      var k = state.blocks[j];
      if (k.hp <= 0) {
        k.vy += L.gravity * TICK; k.x += k.vx * TICK; k.y += k.vy * TICK;
        k.rot += k.vx * TICK * 8;
        var kg = groundAt(L, k.x) - k.h / 2;
        if (k.y > kg) { k.y = kg; k.vy = 0; k.vx *= 0.9; }
      }
    }
  }

  function advance(state, dtMs) {
    state.acc += dtMs;
    var ran = 0;
    while (state.acc >= TICK) { state.acc -= TICK; step(state); ran++; }
    return ran;
  }

  function settled(state) {
    // Unconditional round cap: a trajectory that never contacts a block must
    // still end the round. Checked before the impact rule so that no launch
    // can be unbounded by construction.
    if (state.t >= ROUND_CAP_MS) return true;
    // epsilon guards against float accumulation drift in state.t over many
    // fixed-timestep ticks (e.g. 899.9999999999991 instead of 900).
    return state.firstImpact !== null && (state.t - state.firstImpact) >= SETTLE_MS - 1e-6;
  }

  function toppled(state) {
    return state.blocks.filter(function (b) { return b.hp <= 0; }).length;
  }


  /* ==========================================================================
     PART 2: RENDERER AND INPUT

     Everything below is presentation. It reads the world through the SAME
     functions the physics uses and never re-derives any of them. In particular
     the floor is drawn by calling groundAt() across the canvas: a second copy
     of the valley formula in here would drift from the physics the first time
     anyone retuned a constant, and the ball would visibly float above or sink
     into the floor the visitor can see.
     ========================================================================== */

  /* Every colour here is a token off the quiz palette this page forks. Where a
     scene needs depth it varies ALPHA on a real token, because compositing a
     brand colour keeps it a brand colour, while a darkened near-miss hex reads
     as a page that sits next to the brand rather than inside it. Do not invent
     a shade. Available: #00BAEE #005066 #CCF4FF #0092BA #B3ECFF #7FD8F2
     #3A4A4F #DEDEDE #FFE6EF #D3FFEF #FFB703 #FFFFFF. */
  var SKY_TOP     = '#005066';                 // deep teal overhead
  var SKY_BOT     = '#0092BA';                 // lighter at the horizon
  var GROUND_FILL = '#005066';                 // solid earth under the valley
  var GROUND_LINE = '#00BAEE';
  var BLOCK_FILL  = '#FFB703';
  var BLOCK_DOWN  = 'rgba(255,183,3,0.42)';    // #FFB703 knocked back, toppled
  var BLOCK_LINE  = '#005066';
  var SLING_LINE  = '#D3FFEF';
  var AIM_COLOR   = 'rgba(255,255,255,0.78)';  // #FFFFFF, 31 uses in the forked source
  var BAND_COLOR  = 'rgba(211,255,239,0.85)';  // #D3FFEF, softened

  var BALL_R       = 0.035;  // sprite radius, normalised to canvas WIDTH
  var Y_APEX       = 0.46;   // highest normalised y the view promises to show
  var GROUND_H     = 0.66;   // where the valley low point sits, as a fraction of host height
  var VIEW_PAD     = 14;     // px kept clear above the apex and below the floor
  var FLOOR_STEP   = 3;      // px between floor samples
  var MAX_FRAME_MS = 100;    // a backgrounded tab must not fast-forward a round
  var MAX_PULL     = 0.30;   // a pull this long, in normalised units, is full power
  var AIM_DOTS     = 26;
  var AIM_DOT_MS   = 52;

  /* --------------------------------------------------------------------------
     VILLAIN LABELS. Paint only.

     The bloat fiction is the whole reason this is a branded game rather than a
     generic one, so the blocks carry the four words the live quiz already uses.
     Nothing below reads or writes block geometry, hp, velocity or any physics
     constant: it runs after the box is painted and changes only pixels.

     LABEL_MIN_PX is a legibility floor, not a style choice. A 390px phone puts
     S at 390, so a 0.07 wide block is about 27px across and a five letter word
     fitted inside it lands near 7px. That is not readable, so a word that
     cannot meet the floor inside its own block is not drawn there at all; it
     is set once, at a readable size, in the empty sky directly above the stack
     (see drawLabelPlate). On a wide viewport the words fit inside the bricks
     and no plate is drawn.
     -------------------------------------------------------------------------- */
  var WORDS        = ['BLOAT', 'GAS', 'SLUGGISH', 'HEAVY'];
  var LABEL_FAMILY = 'system-ui, -apple-system, Helvetica, Arial, sans-serif';
  var LABEL_MIN_PX = 9;      // below this a word is not readable, so it is not drawn there
  var LABEL_INK    = '#005066';   // on the amber brick
  var LABEL_PLATE  = '#FFFFFF';   // in the sky, over backdrop art
  var LABEL_EDGE   = '#005066';   // its outline, so it reads on a bright backdrop

  var SPRITES = [
    { fill: '#00BAEE', outline: '#005066', form: 'round-twintail' },
    { fill: '#005066', outline: '#00BAEE', form: 'stout-capsule' },
    { fill: '#FFB703', outline: '#005066', form: 'oval-longtail' },
    { fill: '#D3FFEF', outline: '#005066', form: 'spiky-corona' },
    { fill: '#FFE6EF', outline: '#005066', form: 'tall-rod' }
  ];

  function drawSprite(ctx, s, cx, cy, r) {
    ctx.save(); ctx.translate(cx, cy);
    ctx.fillStyle = s.fill; ctx.strokeStyle = s.outline; ctx.lineWidth = Math.max(2, r * 0.12);
    ctx.beginPath();
    if (s.form === 'tall-rod') ctx.ellipse(0, 0, r * 0.62, r, 0, 0, 6.2832);
    else if (s.form === 'oval-longtail') ctx.ellipse(0, 0, r, r * 0.78, 0, 0, 6.2832);
    else if (s.form === 'stout-capsule') ctx.ellipse(0, 0, r * 0.95, r * 0.8, 0, 0, 6.2832);
    else ctx.arc(0, 0, r, 0, 6.2832);
    ctx.fill(); ctx.stroke();
    if (s.form === 'spiky-corona') {
      for (var i = 0; i < 10; i++) {
        var a = i / 10 * 6.2832;
        ctx.beginPath();
        ctx.moveTo(Math.cos(a) * r, Math.sin(a) * r);
        ctx.lineTo(Math.cos(a) * r * 1.34, Math.sin(a) * r * 1.34);
        ctx.stroke();
      }
    }
    if (s.form === 'round-twintail' || s.form === 'oval-longtail') {
      ctx.beginPath(); ctx.moveTo(-r * 0.9, 0);
      ctx.quadraticCurveTo(-r * 1.7, -r * 0.4, -r * 2.1, r * 0.1); ctx.stroke();
      if (s.form === 'round-twintail') {
        ctx.beginPath(); ctx.moveTo(-r * 0.9, r * 0.3);
        ctx.quadraticCurveTo(-r * 1.7, r * 0.8, -r * 2.0, r * 0.5); ctx.stroke();
      }
    }
    ctx.fillStyle = '#ffffff';
    ctx.beginPath(); ctx.arc(r * 0.22, -r * 0.2, r * 0.3, 0, 6.2832); ctx.fill();
    ctx.fillStyle = s.outline;
    ctx.beginPath(); ctx.arc(r * 0.3, -r * 0.2, r * 0.14, 0, 6.2832); ctx.fill();
    ctx.restore();
  }

  // The interface for a sprite is { fill, outline, draw(ctx, r) }. The 'form' key is the
  // switch drawSprite reads; 'draw' is the method callers get, so a preview tile
  // can render one sprite without knowing about drawSprite at all.
  function spriteDraw(ctx, r) { drawSprite(ctx, this, 0, 0, r); }
  for (var _si = 0; _si < SPRITES.length; _si++) { SPRITES[_si].draw = spriteDraw; }

  // --------------------------------------------------------------------------
  // The level the section ships with. These are the exact constants the engine
  // suite pins, so the harness, the tests and the live page all read one set.
  // --------------------------------------------------------------------------
  var DEFAULT_LEVEL = {
    gravity: 0.0000014, ground: 0.775,
    valleyX: 0.78, valleyKLeft: 0.085, valleyKRight: 3.0,
    sling: { x: 0.14, y: 0.72 }, maxPower: 0.001,
    blocks: [
      { x: 0.70, y: 0.74, w: 0.07, h: 0.08, hp: 1 },
      { x: 0.70, y: 0.66, w: 0.07, h: 0.08, hp: 1 },
      { x: 0.78, y: 0.74, w: 0.07, h: 0.08, hp: 1 }
    ]
  };

  function num(v, d) { return typeof v === 'number' && isFinite(v) ? v : d; }

  // A round may pass a whole level, a partial one, or nothing at all. Missing
  // fields fall back to DEFAULT_LEVEL. Blocks are deep copied so two rounds can
  // never share mutable debris.
  function levelFrom(round) {
    var L = round || {};
    if (L.level && L.level.blocks) L = L.level;
    var sl = L.sling || {};
    var out = {
      gravity: num(L.gravity, DEFAULT_LEVEL.gravity),
      ground: num(L.ground, DEFAULT_LEVEL.ground),
      valleyX: num(L.valleyX, DEFAULT_LEVEL.valleyX),
      valleyKLeft: num(L.valleyKLeft, DEFAULT_LEVEL.valleyKLeft),
      valleyKRight: num(L.valleyKRight, DEFAULT_LEVEL.valleyKRight),
      maxPower: num(L.maxPower, DEFAULT_LEVEL.maxPower),
      sling: { x: num(sl.x, DEFAULT_LEVEL.sling.x), y: num(sl.y, DEFAULT_LEVEL.sling.y) },
      blocks: []
    };
    // An explicitly empty array is honoured: a level with no blocks is how a
    // round that can only end on the cap gets built, which is a case worth
    // being able to reproduce.
    var src = Object.prototype.toString.call(L.blocks) === '[object Array]' ? L.blocks : DEFAULT_LEVEL.blocks;
    for (var i = 0; i < src.length; i++) {
      var b = src[i];
      out.blocks.push({ x: num(b.x, 0.7), y: num(b.y, 0.74), w: num(b.w, 0.07), h: num(b.h, 0.08), hp: num(b.hp, 1) });
    }
    return out;
  }

  /* --------------------------------------------------------------------------
     DRAG TO VELOCITY

     The gesture is a slingshot pull: press, drag BACK, release. The launch
     vector is the reverse of the pull, so pulling down and to the left throws
     up and to the right. Both the pull and the returned velocity are in the
     engine's own units, normalised to canvas WIDTH on both axes, so the mapping
     does not care how large the canvas is.

     Returns the clamped velocity AND the pull that actually produced it, so the
     renderer can draw the band in the same place the shot will come from. The
     drag preview and the shot are then the same number, and a clamp the visitor
     cannot see does not exist.
     -------------------------------------------------------------------------- */
  function dragToVelocity(level, pullX, pullY) {
    var p = num(level && level.maxPower, DEFAULT_LEVEL.maxPower);
    var k = p / MAX_PULL;
    var len = Math.sqrt(pullX * pullX + pullY * pullY);
    if (len > MAX_PULL) { pullX = pullX / len * MAX_PULL; pullY = pullY / len * MAX_PULL; }
    var vx = -pullX * k;
    var vy = -pullY * k;

    /* ====================================================================
       DO NOT REMOVE THIS CLAMP. IT IS LOAD BEARING.

       The physics core has no containment on the left side of the world. A
       launch with vx < 0 sends the ball off canvas to roughly x = -0.73,
       where it keeps living, hits nothing, and cannot settle until the
       8000ms round cap expires. The visitor watches a dead screen for eight
       seconds and then gets told they toppled nothing.

       So horizontal velocity is clamped here, in the input layer, before it
       can ever reach core.launch. The VECTOR is clamped, the gesture is not
       discarded: a backwards drag still fires, it just fires straight up,
       lands on the left wall of the valley and rolls down into the stack.
       Dropping the gesture instead would make the drag look broken, which
       is a worse bug than a weak shot.
       ==================================================================== */
    if (vx < 0) vx = 0;

    var m = Math.sqrt(vx * vx + vy * vy);
    if (m > p) { vx = vx / m * p; vy = vy / m * p; m = p; }
    // The pull that survives, for the renderer. A rightward drag comes back
    // with pullX 0, so the band visibly refuses to bend that way.
    return { vx: vx, vy: vy, power: p > 0 ? m / p : 0, pullX: -vx / k, pullY: -vy / k };
  }

  function boxPath(ctx, x, y, w, h, r) {
    if (r > w / 2) r = w / 2;
    if (r > h / 2) r = h / 2;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  /* --------------------------------------------------------------------------
     PCGG.play(opts)

     opts = { host, round, ammo, backdrop, onDone }
       host     element the canvas is appended to, sized by CSS
       round    a level, a partial level, or nothing
       ammo     0..4, picks the sprite
       backdrop image URL, or null, or a URL that fails to load
       onDone   called EXACTLY ONCE with { toppled, shots }

     Returns { canvas, state, level, destroy }.
     -------------------------------------------------------------------------- */
  function play(opts) {
    opts = opts || {};
    var host = opts.host;
    if (!host || typeof document === 'undefined') return null;

    var level = levelFrom(opts.round);
    var sprite = SPRITES[Math.abs(opts.ammo | 0) % SPRITES.length];
    var state = makeState(level);

    var canvas = document.createElement('canvas');
    canvas.className = 'pcgg__canvas';
    canvas.style.touchAction = 'none';  // belt and braces: the stylesheet says this too
    var ctx = canvas.getContext('2d');
    host.appendChild(canvas);

    var W = 1, H = 1, S = 1, OX = 0, OY = 0, dpr = 1;
    var floorPts = [];
    var launched = false, done = false, raf = 0, last = 0;
    var dragging = false, dragId = null, pullX = 0, pullY = 0, startX = 0, startY = 0;
    var bg = null, bgReady = false;

    // Which word each brick carries. The offset walks with the round number so
    // all four words are seen across a game even though a stack only holds
    // three bricks; it is computed once here, never per frame, so a brick's
    // word cannot change mid round. A level object (the bonus round) has no
    // number, and falls back to 0.
    var labelOffset = (typeof opts.round === 'number' && isFinite(opts.round)) ?
      (Math.abs(opts.round | 0) % WORDS.length) : 0;

    // Advance width of each word at 100px, measured once. Fitting a word to a
    // brick is then one multiply instead of a measureText per brick per frame.
    var LABEL_W100 = {};
    (function () {
      ctx.save();
      ctx.font = '700 100px ' + LABEL_FAMILY;
      for (var w = 0; w < WORDS.length; w++) {
        LABEL_W100[WORDS[w]] = ctx.measureText(WORDS[w]).width || 1;
      }
      ctx.restore();
    })();

    // A null backdrop is the normal case today: the art is not committed yet.
    // A URL that 404s lands in exactly the same place. Either way bgReady stays
    // false and the sky falls back to a flat brand gradient, with the floor
    // painted its own flat colour underneath, so the scene is always complete.
    if (opts.backdrop) {
      bg = new Image();
      bg.onload = function () { bgReady = !!(bg.width && bg.height); };
      bg.onerror = function () { bgReady = false; bg = null; };
      bg.src = opts.backdrop;
    }

    function fx(n) { return OX + n * S; }
    function fy(n) { return OY + n * S; }
    function toNx(px) { return (px - OX) / S; }
    function toNy(px) { return (px - OY) / S; }

    function resize() {
      dpr = Math.min(2, window.devicePixelRatio || 1);
      var r = host.getBoundingClientRect ? host.getBoundingClientRect() : { width: 0, height: 0 };
      W = Math.max(1, Math.round(r.width || host.clientWidth || 360));
      H = Math.max(1, Math.round(r.height || host.clientHeight || 640));
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      canvas.style.width = W + 'px';
      canvas.style.height = H + 'px';
      // Backing store is dpr times the CSS box, so everything below can be
      // written in CSS pixels. Capped at 2 because a 3x phone doubles the fill
      // cost for no visible gain on a canvas this simple.
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // ONE uniform scale for both axes, because the engine normalises both
      // axes to width. A separate y scale would flatten the valley and squash
      // the sprite. S is the largest scale that still fits the whole normalised
      // x range AND the band from the arc apex down to the valley low point.
      S = Math.min(W, (H - VIEW_PAD * 2) / (level.ground - Y_APEX));
      OX = (W - S) / 2;
      var want = H * GROUND_H - level.ground * S;   // preferred horizon height
      var lo = VIEW_PAD - Y_APEX * S;               // apex must stay on screen
      var hi = H - VIEW_PAD - level.ground * S;     // floor must stay on screen
      OY = want < lo ? lo : (want > hi ? hi : want);

      // Floor sample cache. Rebuilt only on resize, since the valley is static.
      floorPts = [];
      var px = 0;
      while (px < W) { floorPts.push([px, fy(groundAt(level, toNx(px)))]); px += FLOOR_STEP; }
      floorPts.push([W, fy(groundAt(level, toNx(W)))]);
    }

    function drawBackdrop() {
      if (bg && bgReady) {
        var sc = Math.max(W / bg.width, H / bg.height);   // cover, never letterbox
        var dw = bg.width * sc, dh = bg.height * sc;
        ctx.drawImage(bg, (W - dw) / 2, (H - dh) / 2, dw, dh);
        return;
      }
      var g = ctx.createLinearGradient(0, 0, 0, H);
      g.addColorStop(0, SKY_TOP);
      g.addColorStop(1, SKY_BOT);
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, W, H);
    }

    function floorPath() {
      ctx.beginPath();
      ctx.moveTo(floorPts[0][0], floorPts[0][1]);
      for (var i = 1; i < floorPts.length; i++) ctx.lineTo(floorPts[i][0], floorPts[i][1]);
    }

    function drawFloor() {
      floorPath();
      ctx.lineTo(W, H); ctx.lineTo(0, H); ctx.closePath();
      ctx.fillStyle = GROUND_FILL;
      ctx.fill();
      floorPath();
      ctx.strokeStyle = GROUND_LINE;
      ctx.lineWidth = Math.max(2, S * 0.006);
      ctx.lineJoin = 'round';
      ctx.stroke();
    }

    function drawSling() {
      var sx = fx(level.sling.x), sy = fy(level.sling.y);
      var gy = fy(groundAt(level, level.sling.x));
      var arm = Math.max(3, S * 0.012);
      ctx.strokeStyle = SLING_LINE;
      ctx.lineWidth = arm;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(sx, gy); ctx.lineTo(sx, sy + arm);
      ctx.moveTo(sx - S * 0.022, sy - S * 0.012); ctx.lineTo(sx, sy + arm);
      ctx.moveTo(sx + S * 0.022, sy - S * 0.012); ctx.lineTo(sx, sy + arm);
      ctx.stroke();
    }

    // The word set for the bricks that are still standing but too small to
    // carry their own label. Set once, centred over the stack, in the empty
    // band between the arc apex and the top brick. Outlined so it reads over
    // backdrop art as well as over the flat gradient sky. Words leave this
    // line the moment their brick falls, so the line is always exactly the
    // villains still on their feet.
    function drawLabelPlate(words) {
      var minX = 0, maxX = 0, topY = 0, any = false;
      for (var i = 0; i < state.blocks.length; i++) {
        var b = state.blocks[i];
        if (b.hp <= 0) continue;
        if (!any) { minX = b.x - b.w / 2; maxX = b.x + b.w / 2; topY = b.y - b.h / 2; any = true; }
        else {
          if (b.x - b.w / 2 < minX) minX = b.x - b.w / 2;
          if (b.x + b.w / 2 > maxX) maxX = b.x + b.w / 2;
          if (b.y - b.h / 2 < topY) topY = b.y - b.h / 2;
        }
      }
      if (!any) return;

      var text = words.join('   ');
      var px = Math.min(16, Math.max(LABEL_MIN_PX, S * 0.034));
      ctx.save();
      ctx.font = '700 ' + px.toFixed(2) + 'px ' + LABEL_FAMILY;
      var tw = ctx.measureText(text).width;
      var room = W - 16;
      if (tw > room && tw > 0) {
        px = Math.max(LABEL_MIN_PX, px * room / tw);
        ctx.font = '700 ' + px.toFixed(2) + 'px ' + LABEL_FAMILY;
        tw = ctx.measureText(text).width;
      }
      var cx = fx((minX + maxX) / 2);
      if (cx - tw / 2 < 8) cx = 8 + tw / 2;
      if (cx + tw / 2 > W - 8) cx = W - 8 - tw / 2;
      var cy = fy(topY) - px * 0.85 - 6;
      if (cy < px * 0.7 + 4) cy = px * 0.7 + 4;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.lineJoin = 'round';
      ctx.lineWidth = Math.max(2, px * 0.24);
      ctx.strokeStyle = LABEL_EDGE;
      ctx.strokeText(text, cx, cy);
      ctx.fillStyle = LABEL_PLATE;
      ctx.fillText(text, cx, cy);
      ctx.restore();
    }

    function wordFor(i) { return WORDS[(labelOffset + i) % WORDS.length]; }

    // ONE type size for the whole stack, not one per brick: the bricks are the
    // same size, so independently fitted words would come out at three visibly
    // different sizes on identical boxes, which reads as a mistake. The size is
    // the smallest that every standing word needs, so the longest word governs
    // and every brick is set in the same face. Returns 0 when that size falls
    // under the legibility floor, which is the signal to use the plate instead.
    function stackLabelPx() {
      var px = 0, any = false;
      for (var i = 0; i < state.blocks.length; i++) {
        var b = state.blocks[i];
        if (b.hp <= 0) continue;
        var fit = Math.min(b.h * S * 0.60, (b.w * S * 0.84) * 100 / LABEL_W100[wordFor(i)]);
        if (!any || fit < px) { px = fit; any = true; }
      }
      if (!any) return 0;
      return px >= LABEL_MIN_PX ? px : 0;
    }

    function drawBlocks() {
      var lw = Math.max(2, S * 0.005);
      var labelPx = stackLabelPx();
      var spill = [];
      for (var i = 0; i < state.blocks.length; i++) {
        var b = state.blocks[i];
        var bw = b.w * S, bh = b.h * S;
        ctx.save();
        ctx.translate(fx(b.x), fy(b.y));
        if (b.rot) ctx.rotate(b.rot);
        ctx.fillStyle = b.hp > 0 ? BLOCK_FILL : BLOCK_DOWN;
        ctx.strokeStyle = BLOCK_LINE;
        ctx.lineWidth = lw;
        boxPath(ctx, -bw / 2, -bh / 2, bw, bh, Math.min(bw, bh) * 0.2);
        ctx.fill(); ctx.stroke();
        // A toppled brick loses its word outright. Debris tumbles and rotates,
        // and a word printed sideways or upside down on a falling box reads as
        // a rendering fault rather than as a villain going down. The label is
        // dropped the instant hp reaches 0, from the brick AND from the plate.
        if (b.hp > 0) {
          if (labelPx) {
            ctx.font = '700 ' + labelPx.toFixed(2) + 'px ' + LABEL_FAMILY;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillStyle = LABEL_INK;
            ctx.fillText(wordFor(i), 0, 0);
          } else {
            spill.push(wordFor(i));
          }
        }
        ctx.restore();
      }
      if (spill.length) drawLabelPlate(spill);
    }

    function drawBall() {
      var ball = state.ball;
      var r = BALL_R * S;
      var bx = ball.x, by = ball.y;
      if (!launched && dragging) {
        // Pulled back into the band. The pull drawn here is the CLAMPED pull the
        // shot will actually use, so what the visitor aims is what they get.
        var v0 = dragToVelocity(level, pullX, pullY);
        bx += v0.pullX; by += v0.pullY;
        ctx.strokeStyle = BAND_COLOR;
        ctx.lineWidth = Math.max(2, S * 0.008);
        ctx.beginPath();
        ctx.moveTo(fx(level.sling.x) - S * 0.022, fy(level.sling.y) - S * 0.012);
        ctx.lineTo(fx(bx), fy(by) - r);
        ctx.lineTo(fx(level.sling.x) + S * 0.022, fy(level.sling.y) - S * 0.012);
        ctx.stroke();
      }
      // The physics treats the ball as a POINT and pins that point to the floor
      // line, so the drawn circle is lifted by its own radius. Its bottom edge
      // then sits exactly on the floor the visitor can see.
      var cx = fx(bx), cy = fy(by) - r;
      var ang = 0;
      if (launched) {
        var gy = groundAt(level, ball.x);
        if (ball.y >= gy - 0.003) {
          // Grounded: bank to the surface and roll. A wheel of radius BALL_R
          // that has travelled this far has turned through this angle.
          ang = Math.atan(groundSlopeAt(level, ball.x)) + (ball.x - level.sling.x) / BALL_R;
        } else {
          ang = Math.atan2(ball.vy, ball.vx);   // airborne: tail trails the flight
        }
      }
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(ang);
      drawSprite(ctx, sprite, 0, 0, r);
      ctx.restore();
    }

    function drawAim() {
      var v = dragToVelocity(level, pullX, pullY);
      if (v.power <= 0.001) return;
      var sx = level.sling.x, sy = level.sling.y;
      var dot = Math.max(1.6, S * 0.005);
      ctx.fillStyle = AIM_COLOR;
      for (var i = 1; i <= AIM_DOTS; i++) {
        // A closed form ballistic guide. The engine integrates in fixed 16.67ms
        // ticks, so this drifts a hair from the real path late in the arc; it is
        // an aim guide, not a simulation, and it uses the level's own gravity.
        var t = i * AIM_DOT_MS;
        var px = sx + v.vx * t;
        var py = sy + v.vy * t + 0.5 * level.gravity * t * t;
        if (py > groundAt(level, px)) break;
        ctx.globalAlpha = 1 - i / (AIM_DOTS + 4);
        ctx.beginPath();
        ctx.arc(fx(px), fy(py), dot, 0, 6.2832);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    }

    function draw() {
      ctx.clearRect(0, 0, W, H);
      drawBackdrop();
      drawFloor();
      drawSling();
      drawBlocks();
      drawBall();
      if (dragging && !launched) drawAim();
    }

    // ONE SHOT LATCH on the completion callback, separate from `done`. `done`
    // stops the SIMULATION; this stops the CALLBACK. They have to be separate
    // because the settled branch below sets `done` before it draws and before
    // it calls finish, so a throw from either of those lands in the catch with
    // `done` already true and still owing exactly one onDone. destroy() sets
    // it too, which is what keeps the documented "a torn down round never
    // calls onDone" contract true no matter who tears it down.
    var fired = false;

    function finish() {
      stop();
      if (fired) return;
      fired = true;
      if (typeof opts.onDone !== 'function') return;
      // Build the payload defensively. The latch is already set at this point,
      // so a throw while COUNTING the debris would otherwise swallow the only
      // onDone this round will ever get and hang the quiz on a screen with no
      // way forward. A score is worth less than an exit, so a count that
      // cannot be taken is reported as zero and the round still ends.
      var res;
      try { res = { toppled: toppled(state), shots: 1 }; }
      catch (err) { res = { toppled: 0, shots: 1 }; }
      opts.onDone(res);
    }

    function frame(ts) {
      raf = window.requestAnimationFrame(frame);
      try {
        if (!last) last = ts;
        var dt = ts - last;
        last = ts;
        if (dt < 0) dt = 0;
        if (dt > MAX_FRAME_MS) dt = MAX_FRAME_MS;
        if (launched && !done) {
          advance(state, dt);
          // settled() ALONE ends the round. ball.live is set true by launch and is
          // never set false again, so it says nothing about whether the round is
          // over. Poll settled every frame and fire onDone on the first true.
          if (settled(state)) {
            done = true;
            draw();
            finish();
            return;
          }
        }
        draw();
      } catch (err) {
        // The frame reschedules itself at the TOP, so an unguarded throw here
        // used to repeat every frame forever and the round never ended. Every
        // other failure path in this build is defensive; this one now is too.
        // A throw ends the round through the SAME completion path a normal
        // round takes, and the `fired` latch means the visitor still gets
        // exactly one onDone whether the throw came from the simulation, from
        // draw(), or from inside onDone itself.
        done = true;
        finish();
      }
    }

    function stop() { if (raf) { window.cancelAnimationFrame(raf); raf = 0; } }

    function pointOf(e) {
      var r = canvas.getBoundingClientRect();
      return { x: toNx(e.clientX - r.left), y: toNy(e.clientY - r.top) };
    }

    function onDown(e) {
      if (done || launched) return;
      var p = pointOf(e);
      dragging = true; dragId = e.pointerId;
      startX = p.x; startY = p.y; pullX = 0; pullY = 0;
      if (canvas.setPointerCapture) { try { canvas.setPointerCapture(e.pointerId); } catch (err) {} }
      if (e.preventDefault) e.preventDefault();
    }

    function onMove(e) {
      if (!dragging || e.pointerId !== dragId) return;
      var p = pointOf(e);
      pullX = p.x - startX; pullY = p.y - startY;
      if (e.preventDefault) e.preventDefault();
    }

    function onUp(e) {
      if (!dragging || e.pointerId !== dragId) return;
      dragging = false; dragId = null;
      var v = dragToVelocity(level, pullX, pullY);
      pullX = 0; pullY = 0;
      launched = true;
      last = 0;             // do not bill the aiming time to the round clock
      launch(state, v.vx, v.vy);
      if (e.preventDefault) e.preventDefault();
    }

    function onCancel() { dragging = false; dragId = null; pullX = 0; pullY = 0; }
    function onResize() { resize(); }

    function destroy() {
      done = true;          // a torn down round never calls onDone
      fired = true;         // ...and cannot be made to by a late frame either
      stop();
      window.removeEventListener('resize', onResize);
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onCancel);
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
    }

    resize();
    window.addEventListener('resize', onResize);
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onCancel);
    raf = window.requestAnimationFrame(frame);

    return { canvas: canvas, state: state, level: level, destroy: destroy };
  }

  // The 'core' object is the Task 2 contract and must not change shape: a later task builds
  // against these names. Everything after it is additive.
  return { core: { makeState: makeState, launch: launch, step: step,
                   advance: advance, settled: settled, toppled: toppled,
                   groundAt: groundAt, groundSlopeAt: groundSlopeAt },
           play: play, SPRITES: SPRITES, drawSprite: drawSprite,
           dragToVelocity: dragToVelocity, defaultLevel: DEFAULT_LEVEL };
})();

if (typeof module !== 'undefined' && module.exports) { module.exports = PCGG; }
